import { useEffect, useState } from 'react';
import { useAuth } from '../../../../auth/AuthContext';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { PermissionPicker } from '../../../../components/PermissionPicker';
import { Skeleton } from '../../../../components/Skeleton';
import { Stepper, type StepDescriptor } from '../../../../components/Stepper';
import { TextField } from '../../../../components/inputs/TextField';
import { Textarea } from '../../../../components/inputs/Textarea';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { rolesApi } from '../rolesApi';
import { usePermissionCatalog } from '../usePermissionCatalog';
import { sampleRole } from '../utils/sampleRole';

interface CreateRoleModalProps {
  open: boolean;
  onClose: () => void;
  /** Fired after a successful create so the caller can refresh the list. */
  onCreated: () => void;
}

interface CreateStep extends StepDescriptor {
  content: React.ReactNode;
  valid: boolean;
}

/**
 * Create a role — stepped like the create-user form it sits beside.
 *
 * **Permissions are part of the CREATE, not a second errand.** `CreateRoleRequest` carries
 * `PermissionKeys`, and a role that grants nothing is a role nobody can be given yet — so
 * sending the reader back to the row menu to make it useful would be the create form stopping
 * one step short of a usable record. Step 1 is what the role IS; step 2 is what it GRANTS.
 *
 * An empty permission set is still valid, which is why step 2 reports valid either way: a
 * placeholder role somebody fills in later is a legitimate thing to make, and the server
 * accepts it.
 */
export function CreateRoleModal({ open, onClose, onCreated }: CreateRoleModalProps) {
  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [permissionKeys, setPermissionKeys] = useState<string[]>([]);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  /*
    HIDDEN, not disabled — a pure permission gate hides.

    The server refuses a create that carries permission keys from a caller without
    `roles.assign-permissions`, because those rows are that key’s to write. Offering the
    picker to somebody who cannot use it would walk them into a 403 after they had chosen,
    so the step is simply not there. A bare role is still a legitimate thing for a holder of
    `roles.create` alone to make, and they can ask somebody to grant it afterwards.
  */
  const canAssign = useAuth().hasPermission('roles.assign-permissions');

  const { catalog, loading: catalogLoading } = usePermissionCatalog(open && canAssign);

  // Reset on every open so a previous attempt never bleeds into a new one.
  useEffect(() => {
    if (!open) return;

    setStep(0);
    setName('');
    setDescription('');
    setPermissionKeys([]);
    setConfirmed(false);
    setSaving(false);
    setError(null);
  }, [open]);

  const nameValid = name.trim().length > 0;

  const allSteps: CreateStep[] = [
    {
      key: 'role',
      label: 'Role',
      valid: nameValid,
      content: (
        <div className="flex flex-col gap-4">
          <TextField
            id="create-role-name"
            label="Name"
            required
            value={name}
            onChange={(event) => setName(event.target.value)}
            error={error?.fieldError('name')}
            description="Letters, digits, spaces, hyphens and underscores only. It must not match an existing role."
            disabled={saving}
            maxLength={100}
          />

          {/*
            A free note about the record as a whole, so it is full width and a `<Textarea>`
            rather than a one-line field — and it carries the ceiling that component requires,
            or a long description grows the body until the confirm tick scrolls out of reach.
          */}
          <Textarea
            id="create-role-description"
            label="Description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            error={error?.fieldError('description')}
            description="What this role is for. Shown beside it wherever the role is listed."
            disabled={saving}
            maxLength={500}
            minRows={2}
            maxRows={6}
          />
        </div>
      ),
    },
    {
      key: 'permissions',
      label: 'Permissions',
      // Valid with nothing ticked: the server accepts an empty set, and a role somebody
      // fills in later is a legitimate thing to create.
      valid: true,
      content: catalogLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <PermissionPicker
          idPrefix="create-role-permissions"
          suiteFilter
          catalog={catalog}
          value={permissionKeys}
          onChange={setPermissionKeys}
          disabled={saving}
          description="What every account holding this role may do. It can be changed afterwards from the role's own Permissions action."
        />
      ),
    },
  ];

  // The permissions step drops out entirely for a reader who cannot grant any.
  const steps = canAssign ? allSteps : allSteps.slice(0, 1);

  const isLastStep = step === steps.length - 1;
  const canSave = steps.every((s) => s.valid) && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      await rolesApi.create({
        name: name.trim(),
        description: description.trim() ? description.trim() : null,
        permissionKeys,
      });

      onCreated();
      onClose();
    } catch (caught) {
      const failure = caught instanceof ApiError
        ? caught
        : new ApiError('Could not create the role.', 0);

      setError(failure);

      /*
        A stepped form owes the reader the STEP as well as the field. Rejected on step 2 over
        a name they cannot see — a duplicate is the likeliest refusal here — they would have
        to walk back through the wizard to find it.
      */
      focusValidationError(failure, {
        idPrefix: 'create-role',
        stepOf: (field) => (field.toLowerCase().startsWith('permission') ? 1 : 0),
        onStep: setStep,
      });
    } finally {
      setSaving(false);
    }
  }

  /** Fills every required field so the primary action enables on the first try. */
  function autogenerate() {
    const sample = sampleRole();
    setName(sample.name);
    setDescription(sample.description);

    /*
      The OPTIONAL half is filled too, which is the floor this contract actually sets: a role
      created with a name and nothing else produces a profile of em dashes and exercises
      neither the picker nor anything downstream of it.

      Read keys only, and only a handful — a generated role that could delete records is a
      sharper object than a test button should be leaving lying around.
    */
    if (!canAssign) return;

    const readKeys = catalog
      .flatMap((module) => module.permissions)
      .filter((permission) => permission.key.endsWith('.read'))
      .slice(0, 5)
      .map((permission) => permission.key);

    setPermissionKeys(readKeys);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create role"
      size="3xl"
      id="create-role-modal"
      confirm={
        isLastStep
          ? {
              id: 'create-role-confirm',
              checked: confirmed,
              onChange: setConfirmed,
              disabled: saving,
            }
          : undefined
      }
      autogenerate={{
        id: 'create-role-autogenerate',
        onGenerate: autogenerate,
        disabled: saving || catalogLoading,
      }}
      footer={
        <>
          <Button id="create-role-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>

          {/* Back renders from the second step on, and is never gated on validity: going back
              is how an invalid step gets fixed. */}
          {step > 0 ? (
            <Button
              id="create-role-back"
              variant="secondary"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={saving}
            >
              Back
            </Button>
          ) : null}

          {!isLastStep ? (
            <Button
              id="create-role-next"
              variant="secondary"
              onClick={() => setStep((s) => Math.min(steps.length - 1, s + 1))}
              disabled={!steps[step].valid}
            >
              Next
            </Button>
          ) : null}

          <Button
            id="create-role-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
            {Object.entries(error.fields).map(([field, messages]) => (
              <p key={field} className="mt-1 text-xs text-red-600">
                {messages.join(' ')}
              </p>
            ))}
          </div>
        ) : null}

        {/* A stepper over ONE step is chrome reporting a position nobody can move from. */}
        {steps.length > 1 ? (
          <Stepper
            id="create-role"
            steps={steps.map(({ key, label }) => ({ key, label }))}
            activeIndex={step}
            onJump={(index) => {
              // Forward movement requires every earlier step to be valid; back is free.
              if (index <= step || steps.slice(0, index).every((s) => s.valid)) {
                setStep(index);
              }
            }}
          />
        ) : null}

        {/*
          The tab role and its label belong to the STEPPER, so they go when it does. A reader
          without `roles.assign-permissions` sees one step and no stepper, and a `tabpanel`
          labelled by a step heading nobody rendered is a dangling `aria-labelledby` — it
          announces nothing and reads as wired.
        */}
        <div
          id={`create-role-panel-${steps[step].key}`}
          role={steps.length > 1 ? 'tabpanel' : undefined}
          aria-labelledby={steps.length > 1 ? `create-role-step-${steps[step].key}` : undefined}
        >
          {steps[step].content}
        </div>
      </div>
    </Modal>
  );
}
